import { formatLocalDate, formatLocalDateTime } from "../../utils/formatDate";
import { reduzirNome } from "../../utils/reduzirNome";

function CardWrapper({ title, icon, children, accent }) {
    const accentClass = {
        teal: "border-l-teal-500",
        blue: "border-l-blue-600",
        purple: "border-l-violet-600",
        amber: "border-l-amber-500",
    }[accent] ?? "border-l-teal-500";

    const iconClass = {
        teal: "text-teal-500",
        blue: "text-blue-600",
        purple: "text-violet-600",
        amber: "text-amber-500",
    }[accent] ?? "text-teal-500";

    return (
        <div className={`rounded-xl border border-border bg-card shadow-card border-l-[3px] ${accentClass} p-3`}>
            <h4 className={`flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-foreground mb-3 ${iconClass}`}>
                <span className="text-sm">{icon}</span>
                <span className="text-foreground">{title}</span>
            </h4>
            <div className="flex flex-col gap-1.5">
                {children}
            </div>
        </div>
    );
}

function Item({ label, value, highlight }) {
    return (
        <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{label}</span>
            <span className={
                highlight
                    ? "font-bold text-foreground bg-success/10 px-2 py-0.5 rounded-full text-[11px]"
                    : "font-medium text-foreground"
            }>
                {value ?? "—"}
            </span>
        </div>
    );
}

function Badge({ label, className }) {
    return (
        <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold tracking-wide ${className}`}>
            {label}
        </span>
    );
}

function TimeLine({ planned, actual, labelP, labelA }) {
    return (
        <div className="flex flex-col gap-1">
            <div className="flex justify-between text-[11px]">
                <span className="text-muted-foreground/70">{labelP}</span>
                <span className="text-muted-foreground font-medium">{formatLocalDateTime(planned)}</span>
            </div>
            <div className="flex justify-between text-[11px]">
                <span className="text-teal-500 font-semibold">{labelA}</span>
                <span className="text-teal-700 font-bold">{formatLocalDateTime(actual)}</span>
            </div>
        </div>
    );
}

function AvatarChip({ nome, cargo }) {
    const initials = nome?.split(" ").slice(0, 2).map((n) => n[0]).join("");

    return (
        <div className="flex items-center gap-2 py-1">
            <div className="w-7 h-7 rounded-full bg-teal-600 from-teal-500 to-blue-600 flex items-center justify-center text-white font-bold text-[10px] shrink-0">
                {initials}
            </div>
            <div className="min-w-0">
                <p className="text-[11px] font-bold text-foreground leading-tight truncate">{nome}</p>
                <p className="text-[10px] text-muted-foreground">{cargo}</p>
            </div>
        </div>
    );
}

export default function ResumoRota({ rota }) {
    return (
        <div >
            {/* Grid de cards */}
            <div className="p-2 grid grid-cols-3 gap-3">
                {/* Identificação */}
                <CardWrapper title="Identificação" icon="🔖" accent="teal">
                    <Item label="Carga" value={rota?.carga} highlight />
                    <Item label="Transporte" value={rota?.transporte} />
                    <Item label="Rota GM Carga" value={rota?.rotaGreenmileCarga} />
                    <Item label="Rota GM Transporte" value={rota?.rotaGreenmileTransporte} />
                    <Item label="Filial" value={rota?.filialNome} highlight />
                </CardWrapper>

                {/* Entregas & Volume */}
                <CardWrapper title="Entregas & Volume" icon="📦" accent="blue">
                    <Item label="Total Entregas" value={rota?.totalEntregas} highlight />
                    <Item label="Total Clientes" value={rota?.entregaVolumeTotalCliente} />
                    <Item label="Vol. Realizado" value={`${rota?.rotaGreenmileVolumeRealizado} m³`} />
                    <Item label="Cubos" value={rota?.entregaVolumeCubos} />
                    <Item label="Drop Size Cubo" value={rota?.entregaVolumeDropSizeCubo} />
                    <Item label="Cx. Unitárias" value={rota?.entregaVolumeCaixasUnitarias} />
                    <Item label="Taxa Ocupação" value={rota?.entregaVolumeTaxaOcupacao} highlight />
                </CardWrapper>

                {/* Horários */}
                <CardWrapper title="Horários" icon="🕐" accent="purple">
                    <TimeLine
                        planned={(rota?.rotaGreenmileSaidaPlanejadaOriginal) + ':00'}
                        actual={rota?.rotaGreenmileSaidaRealizada}
                        labelP="Saída Planejada"
                        labelA="Saída Realizada"
                    />
                    <hr className="border-dashed border-border my-1.5" />
                    <TimeLine
                        planned={(rota?.rotaGreenmileChegadaPlanejadaOriginal) + ':00'}
                        actual={rota?.rotaGreenmileChegadaRealizada}
                        labelP="Chegada Planejada"
                        labelA="Chegada Realizada"
                    />
                    <hr className="border-dashed border-border my-1.5" />
                    <Item label="Data Entrega" value={formatLocalDate(rota?.entregaVolumeDataEntrega)} />
                    <Item label="Data Criação Vol." value={formatLocalDate(rota?.entregaVolumeDataCriacao)} />
                </CardWrapper>

                {/* Veículo */}
                <CardWrapper title="Veículo" icon="🚗" accent="amber">
                    <Item label="Placa" value={rota?.veiculoPlaca} highlight />
                    <Item label="Código Solar" value={rota?.veiculoCodigoSolar} />
                    <Item label="Código York" value={rota?.veiculoCodigoYork ?? "—"} />
                </CardWrapper>

                {/* Equipe — full width */}
                <div className="col-span-2">
                    <CardWrapper title="Equipe" icon="👥" accent="teal">
                        <div className="grid grid-cols-3 gap-3 pt-1">
                            <div>
                                <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">Motorista</p>
                                <AvatarChip nome={rota?.motoristaNome} cargo={rota?.motoristaCargo} />
                            </div>
                            <div>
                                <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">Auxiliar 1</p>
                                <AvatarChip nome={rota?.auxiliar1Nome} cargo="Auxiliar de Entrega" />
                            </div>
                            {rota?.auxiliar2Nome &&
                                <div>
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">Auxiliar 2</p>
                                    <AvatarChip nome={rota?.auxiliar2Nome} cargo="Auxiliar de Entrega" />
                                </div>
                            }
                        </div>
                    </CardWrapper>
                </div>
            </div>

            {/* Footer */}
            <div className="border-t border-border bg-muted px-5 py-2.5 flex items-center justify-between text-[11px] text-muted-foreground">
                <span>ID interno: <b className="text-foreground">{rota?.id}</b></span>
                <span>
                    {rota?.temRetornos && (
                        <Badge label="↩ Com Retornos" className="text-amber-700 bg-amber-100" />
                    )}
                </span>
                <span>Descrição: <b className="text-foreground">{rota?.rotaGreenmileDescricaoCarga}</b></span>
            </div>
        </div>
    );
}
